import {
    FaCalendar, FaSitemap, FaCheckCircle, FaCircle, FaGripVertical, FaUserCircle,
    FaEye, FaPlus, FaEdit
} from 'react-icons/fa';

/**
 * Format date as "Day Month Year" — kept local so the card has no external
 * formatter dependency. Mirrors the helper in ProjectDetailPage.jsx.
 */
const formatDate = (dateValue) => {
    if (!dateValue) return '';
    const d = new Date(dateValue);
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
};

/**
 * ProjectKanbanCard
 * Compact, draggable Kanban card used inside a swimlane. Renders task title,
 * due-date, subtask progress, assignee, and quick actions. Subtasks are
 * inlined as draggable chips so users can reparent via drag-and-drop.
 *
 * Kept thin and stateless — all mutations bubble up via callbacks so the
 * parent (ProjectDetailPage) remains the single source of truth.
 */
const ProjectKanbanCard = ({
    task,
    subtasks = [],
    isInCompletedColumn,
    draggedTask,
    getUserName,
    onDragStart,
    onDragOver,
    onDropOnTask,
    onSubtaskDragStart,
    onOpenDetail,
    onCreateSubtask,
    onEditTask
}) => {
    const isCompleted = isInCompletedColumn || task.status === 'Closed' || task.status === 'Completed';
    const completedSubtasks = subtasks.filter(s => s.status === 'Closed' || s.status === 'Completed').length;
    const subtaskPct = subtasks.length > 0 ? (completedSubtasks / subtasks.length) * 100 : 0;

    return (
        <div
            className={`kanban-card ${isCompleted ? 'completed' : ''} ${subtasks.length > 0 ? 'has-subtasks' : ''} ${draggedTask?.id !== task.id ? 'drop-zone' : ''}`}
            draggable
            onDragStart={(e) => onDragStart(e, task, false)}
            onDragOver={(e) => { e.stopPropagation(); onDragOver(e); }}
            onDrop={(e) => { e.stopPropagation(); onDropOnTask(e, task); }}
        >
            <div className="card-header">
                <span className={`priority-dot priority-${task.priority?.toLowerCase()}`}></span>
                <span className="card-title">{task.title || task.description || 'Untitled'}</span>
            </div>

            <div className="card-meta">
                {task.expectedCompletionDate && (
                    <span className={`due-date ${new Date(task.expectedCompletionDate) < new Date() && !isCompleted ? 'overdue' : ''}`}>
                        <FaCalendar /> {formatDate(task.expectedCompletionDate)}
                    </span>
                )}
            </div>

            {subtasks.length > 0 && (
                <div className="card-subtask-progress">
                    <div className="subtask-progress-header">
                        <FaSitemap />
                        <span>{completedSubtasks}/{subtasks.length}</span>
                    </div>
                    <div className="subtask-progress-bar">
                        <div className="subtask-progress-fill" style={{ width: `${subtaskPct}%` }} />
                    </div>
                </div>
            )}

            {subtasks.length > 0 && (
                <div className="card-subtasks">
                    <div className="subtasks-list">
                        {subtasks.map(subtask => (
                            <div
                                key={subtask.id}
                                className={`subtask-item draggable ${subtask.status === 'Closed' || subtask.status === 'Completed' ? 'completed' : ''}`}
                                draggable
                                onDragStart={(e) => { e.stopPropagation(); onSubtaskDragStart(e, subtask); }}
                                onClick={(e) => { e.stopPropagation(); onOpenDetail(subtask); }}
                            >
                                <FaGripVertical className="drag-handle" />
                                {subtask.status === 'Closed' || subtask.status === 'Completed' ? <FaCheckCircle /> : <FaCircle />}
                                <span>{subtask.title || subtask.description}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <div className="card-footer">
                <div className="card-assignee">
                    <FaUserCircle className="assignee-icon" />
                    <span className="assignee-name">{getUserName(task.assignedToUserID)}</span>
                </div>
                <div className="card-actions">
                    <button
                        className="btn-icon btn-view"
                        onClick={(e) => { e.stopPropagation(); onOpenDetail(task); }}
                        title="View details"
                    >
                        <FaEye />
                    </button>
                    <button
                        className="btn-icon btn-add"
                        onClick={(e) => { e.stopPropagation(); onCreateSubtask(task.id); }}
                        title="Add subtask"
                    >
                        <FaPlus />
                    </button>
                    <button
                        className="btn-icon btn-edit"
                        onClick={(e) => { e.stopPropagation(); onEditTask(task); }}
                        title="Edit task"
                    >
                        <FaEdit />
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ProjectKanbanCard;
